import {
  UpdateThresholdTailoring,
  zSupersedeTailoringSchema,
} from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/api-types';
import {
  FinancialDriver,
  FinancialItem,
  FinancialMaterialityLevel,
  GenericLevel,
  MaterialityActualness,
  MaterialityTimeHorizon,
  MaterialityType,
  MaterialityValence,
  MaterialityValueChain,
} from './types';
import { SustainabilityMatterId } from './types-matters';
import { z } from 'zod';

export enum TailoringType {
  REMOVE_FINANCIAL = 'remove-fm',
  REMOVE_IMPACT = 'remove-im',
  PATCH_FINANCIAL = 'patch-fm',
  PATCH_FINANCIAL_EFFECT = 'patch-fe',
  PATCH_IMPACT = 'patch-im',
  THRESHOLD_IMPACT = 'threshold-im',
  THRESHOLD_FINANCIAL = 'threshold-fm',
  THRESHOLD_EFFECTS = 'threshold-fe',
}
/**
 * A single fix to parts of CSRD results with mandatory reasoning
 */
export type CsrdResultTailoring = PatchTailoring | RemoveTailoring | ThresholdTailoring;

export type CsrdResultTailoringByType = {
  [TailoringType.REMOVE_IMPACT]: RemoveImpactTailoring;
  [TailoringType.REMOVE_FINANCIAL]: RemoveFinancialTailoring;
  [TailoringType.PATCH_IMPACT]: PatchImpactTailoring;
  [TailoringType.PATCH_FINANCIAL]: PatchFinancialTailoring;
  [TailoringType.PATCH_FINANCIAL_EFFECT]: PatchFinancialEffectTailoring;
  [TailoringType.THRESHOLD_IMPACT]: ThresholdImpactTailoring;
  [TailoringType.THRESHOLD_FINANCIAL]: ThresholdFinancialTailoring;
  [TailoringType.THRESHOLD_EFFECTS]: ThresholdEffectTailoring;
};

/**
 * Fix the results by providing a new impact, risk or opportunity
 */
export type PatchTailoring = PatchFinancialTailoring | PatchImpactTailoring | PatchFinancialEffectTailoring;

/**
 * Fix results by removing impact, risk or opportunity
 */
export type RemoveTailoring = RemoveFinancialTailoring | RemoveImpactTailoring;

/**
 * Set materiality threshold for impact, financial and financial effect materialities
 */
export type ThresholdTailoring = ThresholdImpactTailoring | ThresholdFinancialTailoring | ThresholdEffectTailoring;

export type MatterValenceSpecificTailoringType =
  | TailoringType.PATCH_FINANCIAL
  | TailoringType.PATCH_IMPACT
  | TailoringType.REMOVE_FINANCIAL
  | TailoringType.REMOVE_IMPACT;

export type DimensionlessTailoringType =
  | TailoringType.THRESHOLD_EFFECTS
  | TailoringType.THRESHOLD_FINANCIAL
  | TailoringType.THRESHOLD_IMPACT;

type TailoringBase = {
  valence: MaterialityValence;
  matterId: SustainabilityMatterId;
  reasoning: string;
};

export type RemoveFinancialTailoring = TailoringBase & {
  type: TailoringType.REMOVE_FINANCIAL;
  materialityType: MaterialityType.FINANCIAL;
  driver: FinancialDriver;
  financialItem: FinancialItem;
  /**
   * References the description of the removed risk or opportunity. Currently we don't have a better
   * key available.
   */
  riskOrOpportunityDescription: string;
};

export type RemoveImpactTailoring = TailoringBase & {
  type: TailoringType.REMOVE_IMPACT;
  materialityType: MaterialityType.IMPACT;
  /**
   * References the removed impact's description. Kept for display in the activity log
   * and for matching legacy rows that don't carry `impactTopicId`. New tailorings should
   * always set `impactTopicId` as well — the matcher prefers that for stable identification
   * (PLT-4614).
   */
  impactDescription: string;
  /**
   * Stable identifier of the impact topic the user disabled. Optional for backward
   * compatibility with rows created before PLT-4614 — new rows created by the UI always
   * carry this when the disabled impact has an associated impact topic. The matcher
   * prefers `impactTopicId`; otherwise it falls back to matching `impactDescription`
   * against the result's impact-topic name (or its raw description as a last resort).
   */
  impactTopicId?: string | undefined;
};

export type PatchFinancialTailoring = TailoringBase & {
  type: TailoringType.PATCH_FINANCIAL;
  description: string;
  materialityType: MaterialityType.FINANCIAL;
  probability: FinancialMaterialityLevel;
  magnitude: FinancialMaterialityLevel;
  timeHorizon: MaterialityTimeHorizon;
  driver: FinancialDriver;
  financialItem: FinancialItem;
};

/**
 * Risk or opportunity where users can specify the probability & magnitude ether as
 * level or more granural percentage values.
 */
export type PatchFinancialEffectTailoring = TailoringBase & {
  type: TailoringType.PATCH_FINANCIAL_EFFECT;
  description: string;
  materialityType: MaterialityType.FINANCIAL;
  timeHorizon: MaterialityTimeHorizon;
  driver: FinancialDriver;
  financialItem: FinancialItem;
  ratioOfItemAffected?: number | undefined;
} & ExclusiveMagnitude &
  ExclusiveProbability;

export type ExclusiveMagnitude = { magnitudeLevel: FinancialMaterialityLevel } | { magnitudeValue: number };
export type ExclusiveProbability = { probabilityLevel: FinancialMaterialityLevel } | { probabilityValue: number };

export type PatchImpactTailoring<V extends MaterialityValence = MaterialityValence> = TailoringBase & {
  type: TailoringType.PATCH_IMPACT;
  description: string;
  materialityType: MaterialityType.IMPACT;
  scaleLevel: GenericLevel;
  probabilityLevel: GenericLevel;
  scopeLevel: GenericLevel;
  valueChain: MaterialityValueChain;
  timeHorizon: MaterialityTimeHorizon;
  actualness: MaterialityActualness;
} & PatchValenceSpecific[V];

type PatchValenceSpecific = {
  [MaterialityValence.NEGATIVE]: { valence: MaterialityValence.NEGATIVE; irremediabilityLevel: GenericLevel };
  [MaterialityValence.POSITIVE]: { valence: MaterialityValence.POSITIVE };
};

export type ThresholdImpactTailoring = {
  type: TailoringType.THRESHOLD_IMPACT;
  materialityType: MaterialityType.IMPACT;
  reasoning: string;
  threshold: number;
};

export type ThresholdFinancialTailoring = {
  type: TailoringType.THRESHOLD_FINANCIAL;
  materialityType: MaterialityType.FINANCIAL;
  reasoning: string;
  threshold: number;
};

export type ThresholdEffectTailoring = {
  type: TailoringType.THRESHOLD_EFFECTS;
  materialityType: MaterialityType.FINANCIAL;
  reasoning: string;
  threshold: number;
};

/**
 * An update to a created tailoring
 */
export type CsrdResultTailoringUpdate = UpdatePatchTailoring | UpdateRemoveTailoring | UpdateThresholdTailoring;

/**
 * Keys that are never allowed to change
 */
type CommonUpdateOmits = 'valence' | 'matterId' | 'type' | 'materialityType';

export type UpdatePatchFinancialTailoring = Partial<Omit<PatchFinancialTailoring, CommonUpdateOmits>>;

type UpdatePatchFinancialEffectTailoringBase = Omit<PatchFinancialEffectTailoring, CommonUpdateOmits>;
type UpdateExclusiveMagnitude =
  | { magnitudeLevel: FinancialMaterialityLevel; magnitudeValue: null }
  | { magnitudeLevel: null; magnitudeValue: number };
type UpdateExclusiveProbability =
  | { probabilityLevel: FinancialMaterialityLevel; probabilityValue: null }
  | { probabilityLevel: null; probabilityValue: number };

export type UpdatePatchFinancialEffectTailoring = Partial<
  UpdatePatchFinancialEffectTailoringBase & UpdateExclusiveMagnitude & UpdateExclusiveProbability
>;

export type UpdatePatchImpactTailoring = Partial<Omit<PatchImpactTailoring, CommonUpdateOmits>>;
export type UpdatePatchTailoring =
  | UpdatePatchFinancialTailoring
  | UpdatePatchImpactTailoring
  | UpdatePatchFinancialEffectTailoring;

/**
 * One can only update the reasoning when updating the removal of an impact. Any other update would cause changes to the original IRO.
 */
type UpdateRemoveTailoring = Partial<Omit<RemoveTailoring, CommonUpdateOmits | 'impactDescription'>>;

export type KeysOfTailoringUpdate =
  | keyof UpdatePatchFinancialTailoring
  | keyof UpdatePatchImpactTailoring
  | keyof UpdateRemoveTailoring
  | keyof UpdateThresholdTailoring;

export type SupersedeTailoringPayload = z.infer<typeof zSupersedeTailoringSchema>;
